import type { ValueOf } from '#layers/base/public';
import type { FormSchemaResolved } from '#layers/block-engine/public';
import type { ConnectorOnOptionsChange, ConnectorOptionError, OnOptionsChangeHelpers } from '#layers/data-model/types/data-sources/define';
import * as z from 'zod/v4-mini';
import { blankToNull, isObjectEmpty, nonEmptyString, omitUndefined } from '#layers/base/public';
import { BuiltinBlockIds } from '#layers/data-model/constants/builtin-block-ids';
import { SslMode } from './ssl';

/**
 * Top-level authentication strategy for a relational connector. `password` uses
 * static database credentials; `aws_iam` swaps them for AWS IAM / RDS auth.
 */
export const AuthType = {
	Password: 'password',
	AwsIam: 'aws_iam',
} as const;

export type AuthType = ValueOf<typeof AuthType>;

/**
 * Form-only AWS credential selector. These values are stripped before submit,
 * but use snake_case to match the surrounding IAM config terminology.
 */
export const CredentialMode = {
	AccessKeys: 'access_keys',
	InstanceProfile: 'instance_profile',
} as const;

export type CredentialMode = ValueOf<typeof CredentialMode>;

/** Live form values the AWS IAM field builders read to compute field visibility. */
export interface AwsIamFieldValues {
	authType?: AuthType;
	awsCredentialMode?: CredentialMode;
	connectionString?: string;
	password?: string;
	ssl?: {
		mode?: SslMode;
	};
	aws_iam?: {
		region?: string;
		access_key?: string;
		secret_key?: string;
		role_arn?: string;
	};
}

/**
 * Cap the dropdown menu width so a long option description wraps instead of
 * stretching the form full-width, following the project's `content: 'max-w-*'`
 * convention for select menus. `min-w-0` is required to override the select
 * theme's `min-w-fit`: its fit-content min-width (the full unwrapped line)
 * would otherwise win over `max-w-*` per CSS min/max precedence.
 */
const authSelectUi = { content: 'min-w-0 max-w-96' };

/**
 * Top-level auth-type selector. Always visible; the AWS IAM fields below it
 * react to the chosen value.
 */
function awsAuthTypeField(_values: AwsIamFieldValues) {
	return {
		name: 'authType',
		label: '$t:connectors.common.auth_type',
		description: '$t:connectors.common.auth_type_description',
		block: BuiltinBlockIds.InputSelect,
		default: () => AuthType.Password,
		props: {
			ui: authSelectUi,
			options: [
				{
					value: AuthType.Password,
					label: '$t:connectors.common.auth_type_password',
					description: '$t:connectors.common.auth_type_password_description',
				},
				{
					value: AuthType.AwsIam,
					label: '$t:connectors.common.auth_type_aws_iam',
					description: '$t:connectors.common.auth_type_aws_iam_description',
				},
			],
		},
	};
}

/**
 * AWS IAM credential fields. Each field carries a `hidden` flag derived from the
 * current auth type and credential mode so the form only shows what applies.
 */
function awsIamCredentialFields(values: AwsIamFieldValues) {
	const isIam = values.authType === AuthType.AwsIam;
	const mode = values.awsCredentialMode;

	return [
		{
			name: 'awsCredentialMode',
			label: '$t:connectors.common.aws_credential_mode',
			description: '$t:connectors.common.aws_credential_mode_description',
			block: BuiltinBlockIds.InputSelect,
			hidden: !isIam,
			default: () => CredentialMode.AccessKeys,
			props: {
				ui: authSelectUi,
				options: [
					{
						value: CredentialMode.AccessKeys,
						label: '$t:connectors.common.aws_credential_mode_access_keys',
						description: '$t:connectors.common.aws_credential_mode_access_keys_description',
					},
					{
						value: CredentialMode.InstanceProfile,
						label: '$t:connectors.common.aws_credential_mode_instance_profile',
						description: '$t:connectors.common.aws_credential_mode_instance_profile_description',
					},
				],
			},
		},
		// TODO: replace free-text region with a curated AWS region dropdown
		{
			name: 'aws_iam.region',
			label: '$t:connectors.common.aws_region',
			description: '$t:connectors.common.aws_region_description',
			block: BuiltinBlockIds.Input,
			hidden: !isIam,
			required: isIam,
			props: {
				placeholder: 'us-east-1',
			},
		},
		{
			name: 'aws_iam.access_key',
			label: '$t:connectors.common.aws_access_key',
			description: '$t:connectors.common.aws_access_key_description',
			block: BuiltinBlockIds.Input,
			hidden: !isIam || mode !== CredentialMode.AccessKeys,
			required: isIam && mode === CredentialMode.AccessKeys,
			props: {
				autocomplete: 'off',
			},
		},
		{
			name: 'aws_iam.secret_key',
			label: '$t:connectors.common.aws_secret_key',
			description: '$t:connectors.common.aws_secret_key_description',
			block: BuiltinBlockIds.InputPassword,
			hidden: !isIam || mode !== CredentialMode.AccessKeys,
			required: isIam && mode === CredentialMode.AccessKeys,
			props: {
				autocomplete: 'off',
			},
		},
		{
			name: 'aws_iam.role_arn',
			label: '$t:connectors.common.aws_role_arn',
			description: '$t:connectors.common.aws_role_arn_description',
			block: BuiltinBlockIds.Input,
			hidden: !isIam,
			required: false,
			props: {
				placeholder: 'arn:aws:iam::123456789012:role/my-role',
			},
		},
	];
}

/**
 * Inject the AWS IAM auth controls into a base connector's resolved field list:
 * the auth-type chooser and credential fields wrap the base `password` field
 * (whose `hidden`/`required` flip with the chosen auth type), while
 * connection-string and SSL setup fields are hidden for IAM.
 */
export function applyAwsIamFields<TResult extends object>(
	fields: FormSchemaResolved<TResult>,
	values: AwsIamFieldValues,
): FormSchemaResolved<TResult> {
	const isIam = values.authType === AuthType.AwsIam;

	return fields.flatMap((field) => {
		if ('name' in field && field.name === 'connectionString') {
			return [{ ...field, hidden: isIam }];
		}

		if ('name' in field && field.name === 'password') {
			return [
				awsAuthTypeField(values),
				{ ...field, hidden: isIam, required: !isIam },
				...awsIamCredentialFields(values),
			];
		}

		if ('name' in field && field.name.startsWith('ssl.') && isIam) {
			return [{
				...field,
				hidden: true,
				...(field.name === 'ssl.mode' ? { default: () => SslMode.verifyFull } : {}),
			}];
		}

		return [field];
	}) as FormSchemaResolved<TResult>;
}

/**
 * Inject the AWS IAM auth controls unless the connector is being resolved as a
 * preset's base (see `ConnectorFormSchemaContext.resolvingAsBase`). Generic
 * connectors resolve standalone and show IAM; when the same base is resolved to
 * build a preset's fields the controls are skipped, so they never leak into a
 * non-AWS preset or double-inject on an AWS one (the preset owns its own auth).
 */
export function applyAwsIamFieldsUnlessBase<TResult extends object>(
	fields: FormSchemaResolved<TResult>,
	values: AwsIamFieldValues,
	resolvingAsBase: boolean | undefined,
): FormSchemaResolved<TResult> {
	return resolvingAsBase ? fields : applyAwsIamFields(fields, values);
}

/**
 * Whether `arn` is a well-formed AWS IAM role ARN of the shape
 * `arn:<partition>:iam::<12-digit-account-id>:role/<name>`. Mirrors the backend
 * `validate_role_arn` check by inspecting each segment rather than matching one
 * large regular expression, so every rejection reason stays explicit.
 */
export function isValidRoleArn(arn: string): boolean {
	const segments = arn.split(':');
	if (segments.length < 6) return false;

	// The resource segment may itself contain `:` or `/`, so re-join everything
	// past the account id rather than truncating it.
	const [literal, partition, service, region] = segments;
	const account = segments[4];
	const resource = segments.slice(5).join(':');

	if (literal !== 'arn') return false;
	if (partition !== 'aws' && partition !== 'aws-cn' && partition !== 'aws-us-gov') return false;
	if (service !== 'iam') return false;
	if (region !== '') return false;
	if (!account || !/^\d{12}$/.test(account)) return false;
	if (!resource.startsWith('role/')) return false;

	return resource.slice('role/'.length).length > 0;
}

/** When a role ARN is supplied it must be a well-formed IAM role ARN. */
const roleArnCheck = z.refine<AwsIamObjectValue>(
	(value) => !value.role_arn || isValidRoleArn(value.role_arn),
	{ error: '$t:connectors.common.aws_role_arn_invalid', path: ['role_arn'] },
);

const awsIamObject = z.object({
	region: nonEmptyString(),
	access_key: z.optional(z.string()),
	secret_key: z.optional(z.string()),
	role_arn: z.optional(z.string()),
}).check(roleArnCheck);

/** Parsed shape of the nested `aws_iam` object, shared by its cross-field checks. */
type AwsIamObjectValue = z.infer<typeof awsIamObject>;

/** Nested `aws_iam` schema slice; spread into a base connector schema's shape. */
export const awsIamShape = { aws_iam: z.optional(awsIamObject) };

/**
 * Wire-shape invariants for a relational connector's auth block, applied with
 * `.check(...awsIamRefines)`. These hold regardless of which auth method the
 * form chose, because the wire payload carries no auth-type discriminant:
 * password and AWS IAM are mutually exclusive (`aws_auth_exclusive`), and an
 * `aws_iam` block requires Verify Full SSL (`aws_iam_requires_verify_full`).
 *
 * Whether the chosen method's required credentials are present is a per-mode
 * concern the wire schema cannot express (it cannot see the auth type), so it
 * lives in {@link authSubmitErrors} instead. Without an auth type in scope,
 * "neither present" is ambiguous between an incomplete password form and an
 * incomplete IAM form, and would otherwise report `password_required` against
 * the field that IAM auth hides.
 */
export const awsIamRefines = [
	z.refine<{ password?: string; aws_iam?: unknown }>(
		(value) => value.password == null || value.aws_iam == null,
		{ error: '$t:connectors.common.aws_auth_exclusive', path: ['password'] },
	),
	z.refine<{ aws_iam?: unknown; ssl?: { mode?: SslMode } }>(
		(value) => value.aws_iam == null || value.ssl?.mode === SslMode.verifyFull,
		{ error: '$t:connectors.common.aws_iam_requires_verify_full', path: ['ssl', 'mode'] },
	),
];

/** Wire representation of the `aws_iam` credentials block sent to the engine. */
export type AwsIamWire = z.infer<typeof awsIamObject>;

/**
 * Collapse raw form values into the wire `aws_iam` object: trim each field, drop
 * the blanks, and return `undefined` when nothing was filled in.
 */
export function pruneAwsIam(raw: AwsIamFieldValues['aws_iam']): AwsIamWire | undefined {
	const pruned = omitUndefined({
		region: blankToNull(raw?.region) ?? undefined,
		access_key: blankToNull(raw?.access_key) ?? undefined,
		secret_key: blankToNull(raw?.secret_key) ?? undefined,
		role_arn: blankToNull(raw?.role_arn) ?? undefined,
	});

	return isObjectEmpty(pruned) ? undefined : pruned;
}

/**
 * Fold the chosen auth method into the wire payload: password auth appends the
 * password, while AWS IAM emits a pruned `aws_iam` block for the active mode.
 * The role ARN is optional and, when provided, is included alongside whatever
 * base credentials the mode contributes.
 */
export function applyAuthToWire<TBase extends object>(base: TBase, opts: AwsIamFieldValues) {
	if (opts.authType !== AuthType.AwsIam) {
		return { ...base, password: opts.password };
	}

	const raw = opts.aws_iam;
	const mode = opts.awsCredentialMode;

	// Keep only the fields the active credential mode uses so values left behind by a previous mode never reach the wire.
	const aws_iam = pruneAwsIam({
		region: raw?.region,
		...(mode === CredentialMode.AccessKeys ? { access_key: raw?.access_key, secret_key: raw?.secret_key } : {}),
		role_arn: raw?.role_arn,
	});

	return aws_iam ? { ...enforceAwsIamSsl(base), aws_iam } : base;
}

/** AWS IAM database authentication requires full certificate and hostname verification. */
export function enforceAwsIamSsl<TBase extends object>(
	base: TBase,
): Omit<TBase, 'ssl'> & { ssl: { mode: typeof SslMode.verifyFull } } {
	const rest = { ...base } as Omit<TBase, 'ssl'> & { ssl?: unknown };
	delete rest.ssl;

	return { ...rest, ssl: { mode: SslMode.verifyFull } };
}

/**
 * Keep the live form in the only SSL mode AWS IAM supports. The wire transform
 * also enforces this, but updating the form value keeps hidden SSL state aligned.
 */
function enforceAwsIamSslOnOptionsChange<TOptions extends AwsIamFieldValues>(
	newVal: TOptions,
	helpers: Pick<OnOptionsChangeHelpers<TOptions>, 'setValues' | 'hasChanged'>,
): void {
	if (newVal.authType !== AuthType.AwsIam) return;

	const connectionStringChanged = helpers.hasChanged('connectionString');
	if (newVal.ssl?.mode === SslMode.verifyFull && !connectionStringChanged) return;

	helpers.setValues({
		ssl: { ...newVal.ssl, mode: SslMode.verifyFull },
	} as Partial<TOptions>);
}

/**
 * Compose a connector's normal change handler with the AWS IAM SSL invariant.
 * Runs last so parsed connection strings cannot leave IAM on a weaker SSL mode.
 */
export function withAwsIamSslOnOptionsChange<TOptions extends AwsIamFieldValues>(
	handler?: ConnectorOnOptionsChange<TOptions>,
): ConnectorOnOptionsChange<TOptions> {
	return (newVal, oldVal, helpers) => {
		handler?.(newVal, oldVal, helpers);
		enforceAwsIamSslOnOptionsChange(newVal, helpers);
	};
}

/**
 * Submit-time validation of the chosen auth method's required credentials, as a
 * list of form errors each anchored to the visible field that is missing. This
 * is the sole enforcement of the per-mode credential requirements, which the
 * wire schema cannot express (it carries no auth-type discriminant), so they
 * surface on submit rather than the moment the user switches auth type or
 * credential mode.
 *
 * Password auth requires a password. AWS IAM auth requires a region, plus the
 * access key and secret key in access-keys mode; the role ARN is always optional
 * and instance-profile mode needs only the region.
 */
export function authSubmitErrors(values: AwsIamFieldValues): ConnectorOptionError[] {
	if (values.authType !== AuthType.AwsIam) {
		return blankToNull(values.password) === null
			? [{ name: 'password', message: '$t:connectors.common.password_required' }]
			: [];
	}

	const errors: ConnectorOptionError[] = [];

	if (blankToNull(values.aws_iam?.region) === null) {
		errors.push({ name: 'aws_iam.region', message: '$t:connectors.common.aws_region_required' });
	}

	if (values.awsCredentialMode === CredentialMode.AccessKeys) {
		if (blankToNull(values.aws_iam?.access_key) === null) {
			errors.push({ name: 'aws_iam.access_key', message: '$t:connectors.common.aws_access_key_required' });
		}

		if (blankToNull(values.aws_iam?.secret_key) === null) {
			errors.push({ name: 'aws_iam.secret_key', message: '$t:connectors.common.aws_secret_key_required' });
		}
	}

	return errors;
}
